// JobReport.js (new)
import React from "react";
import { useQuery } from "@apollo/client";
import { QUERY_REPORT_JOBS, QUERY_REPORT_JOBS_LEGACY } from "../utils/graphql/jobs/queries";
import { GenericReportTable } from "../components/reporting/GenericReportTable/GenericReportTable.js";
import SingleJobCard from "../components/SingleJobCard";
import { ModalForm } from "../components/Modal/ModalForm";
import { MatchEngineModal } from "../components/MatchEngineModal";
import { createColumnHelper } from "@tanstack/react-table";


export default function JobReport({ me }) {
    const REPORT_LIMIT = 200;
    const [useLegacyQuery, setUseLegacyQuery] = React.useState(false);

    const pagedResult = useQuery(QUERY_REPORT_JOBS, {
        skip: useLegacyQuery,
        variables: {
            offset: 0,
            limit: REPORT_LIMIT,
        },
        onError: (err) => {
            const hasUnknownArgError = (err?.graphQLErrors || []).some((gqlErr) => {
                const message = gqlErr?.message || "";
                return message.includes('Unknown argument "offset"') || message.includes('Unknown argument "limit"');
            });

            if (hasUnknownArgError) {
                setUseLegacyQuery(true);
            }
        },
    });

    const legacyResult = useQuery(QUERY_REPORT_JOBS_LEGACY, {
        skip: !useLegacyQuery,
    });

    const data = useLegacyQuery ? legacyResult.data : pagedResult.data;
    const refetchJobs = useLegacyQuery ? legacyResult.refetch : pagedResult.refetch;
    const isAdmin = me?.admin;

    const [selectedJob, setSelectedJob] = React.useState(null);
    const [showWideJobModal, setShowWideJobModal] = React.useState(false);
    const [showMatchEngineModal, setShowMatchEngineModal] = React.useState(false);

    const jobs = React.useMemo(() => {
        return data?.jobs?.map(job => ({
            id: job._id,
            title: job.meetingSnapshot?.title,
            start: job.meetingSnapshot?.startDateTime,
            createdBy: job.createdBy?.username,
            applicants: job.applicationCount ?? 0,
            assignedTo: job.assignedTo?.username,
            status: job.active ? "Open" : "Closed",
        })) || [];
    }, [data]);

    const filterFns = React.useMemo(() => ({
        equalsString: (row, columnId, filterValue) => {
            const rowValue = row.getValue(columnId);
            return String(rowValue).toLowerCase() === String(filterValue).toLowerCase();
        },
        equalsDate: (row, columnId, value) => {
            const rowDate = new Date(row.getValue(columnId)).setHours(0, 0, 0, 0);
            const filterDate = new Date(value).setHours(0, 0, 0, 0);
            return rowDate === filterDate;
        },
        beforeDate: (row, columnId, value) => {
            return new Date(row.getValue(columnId)) < new Date(value);
        },
        afterDate: (row, columnId, value) => {
            return new Date(row.getValue(columnId)) >= new Date(value);
        },
        betweenDate: (row, columnId, range) => {
            const d = new Date(row.getValue(columnId));
            return d >= new Date(range.start) && d <= new Date(range.end);
        }
    }), []);

    const columnHelper = React.useMemo(() => createColumnHelper(), []);

    const columns = React.useMemo(() => [
        columnHelper.accessor("title", { header: "Meeting", filterFn: "includesString" }),
        columnHelper.accessor("start", {
            id: "date",
            header: "Date",
            meta: { type: "date" },
            filterFn: (row, columnId, filterValue, table) => {
                if (!filterValue) return true;

                if (filterValue.fn) {
                    return filterFns[filterValue.fn](
                        row,
                        columnId,
                        filterValue.value
                    );
                }

                return true; // no automatic filtering
            },
            cell: info => new Date(info.getValue()).toLocaleString([], {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        }),
        columnHelper.accessor("createdBy", { header: "Submitted by", filterFn: "includesString" }),
        columnHelper.accessor("applicants", {
            header: "Applicants",
            enableColumnFilter: false,
            disableFilters: true,
        }),
        columnHelper.accessor("assignedTo", { header: "Assigned To", filterFn: "includesString" }),
        columnHelper.accessor("status", { header: "Status", filterFn: "equalsString" }),
    ], [columnHelper, filterFns]);


    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const initialFilters = [
        { id: "date", value: { fn: "afterDate", value: today } }
    ];

    const initialSorting = [
        { id: "date", desc: false }
    ];

    return (
        <>
            <GenericReportTable
                data={jobs}
                columns={columns}
                filterFns={filterFns}
                initialFilters={initialFilters}
                initialSorting={initialSorting}
                onRowClick={(job) => {
                    setShowWideJobModal(false);
                    setSelectedJob(job);
                }}
                toolbarRight={
                    isAdmin && (
                        <button className="btn btn-info" onClick={() => setShowMatchEngineModal(true)}>
                            Run Match Engine
                        </button>
                    )
                }
            />

            {selectedJob && (
                <ModalForm
                    title="Sub Request Details"
                    onClose={() => {
                        setShowWideJobModal(false);
                        setSelectedJob(null);
                    }}
                    size={showWideJobModal ? "wide" : "md"}
                >
                    <SingleJobCard
                        me={me}
                        jobId={selectedJob.id}
                        onMetricsToggle={setShowWideJobModal}
                        onClose={() => {
                            setShowWideJobModal(false);
                            setSelectedJob(null);
                            refetchJobs?.();
                        }}
                    />
                </ModalForm>
            )}

            {showMatchEngineModal && (
                <MatchEngineModal onClose={() => setShowMatchEngineModal(false)} />
            )}
        </>
    );
}